import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  toast: (kind: ToastKind, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 3000;

const KIND_STYLES: Record<ToastKind, { icon: typeof CheckCircle2; classes: string; iconClasses: string }> = {
  success: {
    icon: CheckCircle2,
    classes:
      'border-[color-mix(in_oklab,var(--color-success)_45%,var(--surface-line))] bg-[color-mix(in_oklab,var(--color-success)_10%,var(--surface-panel))] text-[color:var(--ink-strong)]',
    iconClasses: 'text-[color:var(--color-success)]',
  },
  error: {
    icon: AlertTriangle,
    classes:
      'border-[color-mix(in_oklab,var(--color-error)_45%,var(--surface-line))] bg-[color-mix(in_oklab,var(--color-error)_10%,var(--surface-panel))] text-[color:var(--ink-strong)]',
    iconClasses: 'text-[color:var(--color-error)]',
  },
  info: {
    icon: Info,
    classes:
      'border-[color-mix(in_oklab,var(--color-info)_45%,var(--surface-line))] bg-[color-mix(in_oklab,var(--color-info)_10%,var(--surface-panel))] text-[color:var(--ink-strong)]',
    iconClasses: 'text-[color:var(--color-info)]',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;
      setItems((prev) => [...prev.slice(-2), { id, kind, message }]);
      window.setTimeout(() => dismiss(id), DEFAULT_DURATION);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
      >
        {items.map((item) => {
          const style = KIND_STYLES[item.kind];
          const Icon = style.icon;
          return (
            <div
              key={item.id}
              role="status"
              className={cn(
                'pointer-events-auto flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm shadow-[var(--shadow-soft)]',
                'animate-[toast-in_180ms_ease-out]',
                style.classes,
              )}
            >
              <Icon size={18} className={cn('mt-px shrink-0', style.iconClasses)} />
              <p className="min-w-0 flex-1 break-words">{item.message}</p>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                className="shrink-0 rounded p-0.5 text-[color:var(--ink-muted)] hover:text-[color:var(--ink-strong)]"
                aria-label="Închide"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return { toast: () => {} };
  }
  return ctx;
}
